import type { Sku } from "../../redistribution/models/Sku.js";
import { resolveRateOfSale } from "./RateOfSaleResolver.ts";
import { buildRateOfSaleAnchors } from "./SkuRateOfSale.ts";

interface UrgencyLabel {
  text: string;
  emoji: string;
  cssClass: string;
}

export interface UrgencyCounts {
  ok: number;
  moderate: number;
  critical: number;
  na: number;
  total: number;
  rateOfSaleDays?: number;
}

const URGENCY_THRESHOLDS = {
  CRITICAL_DAYS: 3,
  MODERATE_DAYS: 14,
} as const;

const URGENCY_LEVELS = {
  NA: 0,
  MODERATE: 1,
  CRITICAL: 2,
} as const;

export function calculateUrgency(
  daysToStockout: number,
  dailyRateOfSale: number
): number {
  if (dailyRateOfSale <= 0) {
    return URGENCY_LEVELS.NA;
  }
  if (daysToStockout <= URGENCY_THRESHOLDS.CRITICAL_DAYS) {
    return URGENCY_LEVELS.CRITICAL;
  }
  if (daysToStockout <= URGENCY_THRESHOLDS.MODERATE_DAYS) {
    return URGENCY_LEVELS.MODERATE;
  }
  return URGENCY_LEVELS.NA;
}

export function urgencyLabel(urgency: number, dailyROS: number): UrgencyLabel {
  if (dailyROS <= 0)
    return { text: "N/A", emoji: "⚪", cssClass: "urgency-na" };
  if (urgency === URGENCY_LEVELS.CRITICAL)
    return { text: "Critical", emoji: "🔴", cssClass: "urgency-critical" };
  if (urgency === URGENCY_LEVELS.MODERATE)
    return { text: "Moderate", emoji: "🟡", cssClass: "urgency-moderate" };
  return { text: "OK", emoji: "🟢", cssClass: "urgency-ok" };
}

export function getUrgencyColors(cssClass: string): {
  background: string;
  color: string;
} {
  switch (cssClass) {
    case "urgency-critical": {
      return { background: "#fdecea", color: "#c0392b" };
    }
    case "urgency-moderate": {
      return { background: "#fff3cd", color: "#856404" };
    }
    case "urgency-na": {
      return { background: "#f0f0f0", color: "#888888" };
    }
    default: {
      return { background: "#eafaf1", color: "#27ae60" };
    }
  }
}

export function computeDaysToStockout(
  forecastQuantityAvailable: number,
  dailyRateOfSale: number
): number {
  return forecastQuantityAvailable > 0 && dailyRateOfSale > 0
    ? Math.floor(forecastQuantityAvailable / dailyRateOfSale)
    : 0;
}

export function computeWeeksOfStock(
  forecastQuantity: number,
  dailyRateOfSale: number
): { daysToStockout: number; weeksOfStock: number } {
  const daysToStockout = computeDaysToStockout(
    forecastQuantity,
    dailyRateOfSale
  );
  return {
    daysToStockout,
    weeksOfStock: Number.parseFloat((daysToStockout / 7).toFixed(2)),
  };
}

export function compareUrgencyCounts(
  left?: Partial<UrgencyCounts>,
  right?: Partial<UrgencyCounts>
): number {
  const priorityKeys: (keyof Omit<
    UrgencyCounts,
    "total" | "rateOfSaleDays"
  >)[] = ["critical", "moderate", "ok", "na"];

  for (const key of priorityKeys) {
    const leftCount = typeof left?.[key] === "number" ? left[key] : 0;
    const rightCount = typeof right?.[key] === "number" ? right[key] : 0;
    const diff = leftCount - rightCount;

    if (diff !== 0) {
      return diff;
    }
  }

  return 0;
}

/**
 * Resolves a SKU's daily rate-of-sale for the selected window.
 *
 * All ROS consumption is routed through the shared resolver: anchors are built
 * at the single ingestion boundary ({@link buildRateOfSaleAnchors}), so real
 * windows (30/60/90 today) return their exact value and every other window is
 * interpolated/extrapolated. Falls back to window 30 when no window is selected.
 */
export function getDailyDemandForRateOfSale(
  sku: Sku,
  rateOfSale?: string | number
): number {
  const requestedWindow =
    rateOfSale == undefined || rateOfSale === "" ? 30 : Number(rateOfSale);
  const targetWindow = Number.isFinite(requestedWindow) ? requestedWindow : 30;

  return resolveRateOfSale(
    buildRateOfSaleAnchors(sku),
    sku.dailyRateOfSale,
    targetWindow
  ).value;
}

export function summarizeUrgencyCounts(
  skus: Sku[],
  rateOfSale?: string | number
): UrgencyCounts {
  const rosDays = Number(rateOfSale ?? 30);
  const counts: UrgencyCounts = {
    ok: 0,
    moderate: 0,
    critical: 0,
    na: 0,
    total: skus.length,
    rateOfSaleDays: rosDays,
  };

  for (const sku of skus) {
    const dailyDemand = getDailyDemandForRateOfSale(sku, rateOfSale);
    const daysToStockout = computeDaysToStockout(
      sku.forecastQuantityAvailable,
      dailyDemand
    );

    switch (
      urgencyLabel(calculateUrgency(daysToStockout, dailyDemand), dailyDemand)
        .text
    ) {
      case "N/A": {
        counts.na += 1;
        break;
      }
      case "Critical": {
        counts.critical += 1;
        break;
      }
      case "Moderate": {
        counts.moderate += 1;
        break;
      }
      default: {
        counts.ok += 1;
      }
    }
  }

  return counts;
}
